'use client';

import { useTranslations } from 'next-intl';
import {
    Users,
    Gift,
    Link as LinkIcon,
    BarChart3,
    ShieldCheck,
    Coins,
} from 'lucide-react';
import Link from 'next/link';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card } from '@/components/ui/Card';
import { type Locale } from '@/lib/i18n/config';

interface ReferralsPageClientProps {
    locale: Locale;
    isLoggedIn: boolean;
}

export default function ReferralsPageClient({ locale, isLoggedIn }: ReferralsPageClientProps) {
    const t = useTranslations();

    const steps = [
        {
            icon: LinkIcon,
            titleKey: 'share.title',
            descKey: 'share.description',
        },
        {
            icon: Users,
            titleKey: 'signup.title',
            descKey: 'signup.description',
        },
        {
            icon: Coins,
            titleKey: 'earn.title',
            descKey: 'earn.description',
        },
    ];

    const benefits = [
        {
            icon: ShieldCheck,
            titleKey: 'secure.title',
            descKey: 'secure.description',
        },
        {
            icon: Gift,
            titleKey: 'free.title',
            descKey: 'free.description',
        },
        {
            icon: BarChart3,
            titleKey: 'track.title',
            descKey: 'track.description',
        },
    ];

    return (
        <div className="min-h-screen flex flex-col">
            <Header locale={locale} />

            <main className="flex-1">
                {/* Hero Section */}
                <section className="bg-gradient-to-br from-[hsl(var(--color-primary)/0.1)] via-[hsl(var(--color-background))] to-[hsl(var(--color-secondary)/0.1)] py-16">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-6 mt-14">
                                <Users className="h-8 w-8 text-indigo-600" />
                            </div>
                            <h1 className="text-3xl md:text-5xl font-bold mb-6 break-words text-balance">
                                {t('referralsPage.title')}
                            </h1>
                            <p className="text-lg text-[hsl(var(--color-muted-foreground))] mb-8 break-words text-balance">
                                {t('referralsPage.description')}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link
                                    href={isLoggedIn ? `/${locale}/dashboard/refer` : `/${locale}/register`}
                                    className="px-6 py-3 rounded-lg bg-[hsl(var(--color-primary))] text-white font-medium w-full sm:w-auto text-center hover:bg-[hsl(var(--color-primary)/0.9)] transition-colors"
                                >
                                    {t('referralsPage.hero.startReferring')}
                                </Link>
                                <Link
                                    href={isLoggedIn ? `/${locale}/dashboard/refer` : `/${locale}/register`}
                                    className="px-6 py-3 rounded-lg border font-medium w-full sm:w-auto text-center hover:bg-muted/50 transition-colors"
                                >
                                    {t('referralsPage.hero.viewStats')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* How It Works */}
                <section className="py-12 bg-[hsl(var(--color-muted)/0.3)]">
                    <div className="container mx-auto px-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {steps.map((step, index) => {
                                const Icon = step.icon;
                                return (
                                    <Card key={index} className="p-6 text-center" hover>
                                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 mb-4">
                                            <Icon className="h-6 w-6 text-indigo-600" />
                                        </div>
                                        <h3 className="font-semibold mb-2 break-words text-balance">{t(`referralsPage.steps.${step.titleKey}`)}</h3>
                                        <p className="text-sm text-[hsl(var(--color-muted-foreground))] break-words">
                                            {t(`referralsPage.steps.${step.descKey}`)}
                                        </p>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Rewards & Benefits */}
                <section className="py-12">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
                            {benefits.map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <Card key={index} className="p-6" hover>
                                        <Icon className="h-6 w-6 text-indigo-600 mb-3" />
                                        <h3 className="font-semibold mb-2 break-words">{t(`referralsPage.benefits.${item.titleKey}`)}</h3>
                                        <p className="text-sm text-[hsl(var(--color-muted-foreground))] break-words">
                                            {t(`referralsPage.benefits.${item.descKey}`)}
                                        </p>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Student Referral Program */}
                <section className="py-16 bg-gradient-to-br from-[hsl(var(--color-primary)/0.05)] via-[hsl(var(--color-background))] to-[hsl(var(--color-secondary)/0.05)]">
                    <div className="container mx-auto px-4">
                        <div className="max-w-5xl mx-auto">
                            {/* Header */}
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-gradient-to-br from-[hsl(var(--color-primary)/0.15)] to-[hsl(var(--color-secondary)/0.15)] mb-6">
                                    <svg className="w-8 h-8 text-[hsl(var(--color-primary))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                                <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[hsl(var(--color-foreground))]">
                                    {t('referralsPage.studentReferralProgram.title')}
                                </h2>
                                <p className="text-lg text-[hsl(var(--color-muted-foreground))] max-w-3xl mx-auto mb-3">{t(`referralsPage.studentReferralProgram.description`)} </p>
                                <p className="text-base font-medium text-[hsl(var(--color-primary))]">
                                    {t(`referralsPage.studentReferralProgram.subdec`)}
                                </p>
                            </div>

                            {/* How It Works */}
                            <div className="mb-12">
                                <div className="flex items-center gap-2 mb-6">
                                    <h3 className="text-2xl font-bold">{t('referralsPage.studentReferralProgram.howItWorks.title')}</h3>
                                </div>
                                <div className="grid md:grid-cols-3 gap-6">
                                    <Card className="p-6 bg-[hsl(var(--color-card))] border-[hsl(var(--color-border))]" hover>
                                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--color-primary)/0.15)] text-[hsl(var(--color-primary))] font-bold text-xl mb-4">1</div>
                                        <h4 className="font-semibold mb-2 text-lg">{t('referralsPage.studentReferralProgram.howItWorks.step1.title')}</h4>
                                        <p className="text-sm text-[hsl(var(--color-muted-foreground))]">{t('referralsPage.studentReferralProgram.howItWorks.step1.description')}</p>
                                    </Card>
                                    <Card className="p-6 bg-[hsl(var(--color-card))] border-[hsl(var(--color-border))]" hover>
                                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--color-secondary)/0.15)] text-[hsl(var(--color-secondary))] font-bold text-xl mb-4">2</div>
                                        <h4 className="font-semibold mb-2 text-lg">{t('referralsPage.studentReferralProgram.howItWorks.step2.title')}</h4>
                                        <p className="text-sm text-[hsl(var(--color-muted-foreground))]">{t('referralsPage.studentReferralProgram.howItWorks.step2.description')}</p>
                                    </Card>
                                    <Card className="p-6 bg-[hsl(var(--color-card))] border-[hsl(var(--color-border))]" hover>
                                        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--color-primary)/0.15)] to-[hsl(var(--color-secondary)/0.15)] text-[hsl(var(--color-primary))] font-bold text-xl mb-4">3</div>
                                        <h4 className="font-semibold mb-2 text-lg">{t('referralsPage.studentReferralProgram.howItWorks.step3.title')}</h4>
                                        <p className="text-sm text-[hsl(var(--color-muted-foreground))]">{t('referralsPage.studentReferralProgram.howItWorks.step3.description')}</p>
                                    </Card>
                                </div>
                            </div>

                            {/* Student Rewards */}
                            <div className="mb-12">
                                <div className="flex items-center gap-2 mb-6">
                                    <h3 className="text-2xl font-bold">{t('referralsPage.studentReferralProgram.rewards.title')}</h3>
                                </div>
                                <div className="grid md:grid-cols-2 gap-6 mb-6">
                                    <Card className="p-6 bg-[hsl(var(--color-card))] border-[hsl(var(--color-border))]">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[hsl(var(--color-primary)/0.2)] flex items-center justify-center text-2xl">👤</div>
                                            <div>
                                                <p className="font-semibold mb-1">{t('referralsPage.studentReferralProgram.rewards.youGet')}</p>
                                                <p className="text-sm text-[hsl(var(--color-muted-foreground))]">{t('referralsPage.studentReferralProgram.rewards.youGetDesc')}</p>
                                            </div>
                                        </div>
                                    </Card>
                                    <Card className="p-6 bg-[hsl(var(--color-card))] border-[hsl(var(--color-border))]">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[hsl(var(--color-secondary)/0.2)] flex items-center justify-center text-2xl">🎉</div>
                                            <div>
                                                <p className="font-semibold mb-1">{t('referralsPage.studentReferralProgram.rewards.friendGet')}</p>
                                                <p className="text-sm text-[hsl(var(--color-muted-foreground))]">{t('referralsPage.studentReferralProgram.rewards.friendGetDesc')}</p>
                                            </div>
                                        </div>
                                    </Card>
                                </div>

                                <Card className="p-6 bg-[hsl(var(--color-card))]">
                                    <p className="font-medium mb-3">{t('referralsPage.studentReferralProgram.rewards.creditsCanBeUsedFor')}</p>
                                    <div className="grid sm:grid-cols-2 gap-2 mb-4">
                                        {['1', '2', '3', '4'].map((idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-sm text-[hsl(var(--color-muted-foreground))]">
                                                <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--color-primary))]"></div>
                                                {t(`referralsPage.studentReferralProgram.rewards.tools.${idx}`)}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex flex-wrap gap-3 pt-3 border-t border-[hsl(var(--color-border))]">
                                        <div className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            {t('referralsPage.studentReferralProgram.rewards.noCreditCard')}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            {t('referralsPage.studentReferralProgram.rewards.creditsNeverExpire')}
                                        </div>
                                    </div>
                                </Card>
                            </div>

                            {/* Why Students Love & Who Can Join */}
                            <div className="grid md:grid-cols-2 gap-6">
                                <Card className="p-6" hover>
                                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                        {t('referralsPage.studentReferralProgram.whyStudentsLove.title')}
                                    </h3>
                                    <ul className="space-y-3">
                                        {['1', '2', '3', '4', '5'].map((idx) => (
                                            <li key={idx} className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[hsl(var(--color-primary)/0.15)] flex items-center justify-center mt-0.5">
                                                    <svg className="w-3 h-3 text-[hsl(var(--color-primary))]" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <span className="text-sm text-[hsl(var(--color-muted-foreground))]">{t(`referralsPage.studentReferralProgram.whyStudentsLove.reasons.${idx}`)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </Card>

                                <Card className="p-6" hover>
                                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                        {t('referralsPage.studentReferralProgram.whoCanJoin.title')}
                                    </h3>
                                    <ul className="space-y-3">
                                        {['1', '2', '3'].map((idx) => (
                                            <li key={idx} className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[hsl(var(--color-secondary)/0.15)] flex items-center justify-center mt-0.5">
                                                    <svg className="w-3 h-3 text-[hsl(var(--color-secondary))]" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                                <span className="text-sm text-[hsl(var(--color-muted-foreground))]">{t(`referralsPage.studentReferralProgram.whoCanJoin.types.${idx}`)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </Card>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Terms */}
                <section className="py-12">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto text-sm text-[hsl(var(--color-muted-foreground))] space-y-3">
                            <h3 className="text-xl font-semibold text-[hsl(var(--color-foreground))] break-words">
                                {t('referralsPage.terms.title')}
                            </h3>
                            <ul className="list-disc pl-6 space-y-2">
                                {['1', '2', '3', '4'].map((item) => (
                                    <li key={item} className="break-words">{t(`referralsPage.terms.items.${item}`)}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Final CTA */}
                <section className="py-14 bg-gradient-to-r from-[hsl(var(--color-primary)/0.1)] to-[hsl(var(--color-secondary)/0.1)]">
                    <div className="container mx-auto px-4 text-center">
                        <h2 className="text-3xl font-bold mb-4 break-words text-balance">
                            {t('referralsPage.cta.title')}
                        </h2>
                        <p className="text-[hsl(var(--color-muted-foreground))] mb-6 break-words text-balance">
                            {t('referralsPage.cta.description')}
                        </p>
                        <Link
                            href={isLoggedIn ? `/${locale}/dashboard/refer` : `/${locale}/register`}
                            className="px-8 py-3 rounded-lg bg-[hsl(var(--color-primary))] text-white font-medium inline-block hover:bg-[hsl(var(--color-primary)/0.9)] transition-colors w-full sm:w-auto"
                        >
                            {t('referralsPage.cta.button')}
                        </Link>
                    </div>
                </section>
            </main>

            <Footer locale={locale} />
        </div>
    );
}
